import { useQuery } from '@tanstack/react-query';
import {
  IRecipe,
  IMenuItem,
  IProcessedMaterial,
  IRawMaterial,
  ManufacturingHistoryWithDetails,
} from 'shared';
import apiClient from '../api/client';

function normalizeManufacturingHistory(
  payload: unknown,
): ManufacturingHistoryWithDetails[] {
  if (Array.isArray(payload)) {
    return payload;
  }
  if (
    payload &&
    typeof payload === 'object' &&
    Array.isArray((payload as { history?: unknown }).history)
  ) {
    return (payload as { history: ManufacturingHistoryWithDetails[] }).history;
  }
  return [];
}

export function useRecipes() {
  const recipesQuery = useQuery({
    queryKey: ['recipes'],
    queryFn: async () => {
      const { data } = await apiClient.get<IRecipe[]>('/recipes');
      return data;
    },
  });

  const menuItemsQuery = useQuery({
    queryKey: ['menu-items'],
    queryFn: async () => {
      const { data } = await apiClient.get<IMenuItem[]>('/menu');
      return data;
    },
  });

  const processedMaterialsQuery = useQuery({
    queryKey: ['processed-materials'],
    queryFn: async () => {
      const { data } = await apiClient.get<
        IProcessedMaterial[] | { materials?: IProcessedMaterial[] }
      >('/processed-materials');

      if (Array.isArray(data)) return data;
      if (data && Array.isArray(data.materials)) return data.materials;
      return [];
    },
  });

  const rawMaterialsQuery = useQuery({
    queryKey: ['raw-materials'],
    queryFn: async () => {
      const { data } = await apiClient.get<IRawMaterial[]>('/materials');
      return data;
    },
  });

  const manufacturingHistoryQuery = useQuery({
    queryKey: ['manufacturing-history'],
    queryFn: async () => {
      const { data } = await apiClient.get<
        ManufacturingHistoryWithDetails[] | { history: ManufacturingHistoryWithDetails[] }
      >(
        '/manufacturing-history',
      );
      return normalizeManufacturingHistory(data);
    },
  });

  return {
    recipes: recipesQuery.data ?? [],
    recipesLoading: recipesQuery.isLoading,
    menuItems: menuItemsQuery.data ?? [],
    menuItemsLoading: menuItemsQuery.isLoading,
    processedMaterials: processedMaterialsQuery.data ?? [],
    processedMaterialsLoading: processedMaterialsQuery.isLoading,
    rawMaterials: rawMaterialsQuery.data ?? [],
    rawMaterialsLoading: rawMaterialsQuery.isLoading,
    manufacturingHistory: manufacturingHistoryQuery.data ?? [],
    manufacturingHistoryLoading: manufacturingHistoryQuery.isLoading,
    isLoading:
      recipesQuery.isLoading ||
      menuItemsQuery.isLoading ||
      processedMaterialsQuery.isLoading ||
      rawMaterialsQuery.isLoading ||
      manufacturingHistoryQuery.isLoading,
  };
}
